// Componente Angular con un formulario para editar los datos de una solicitud de bajas.
// Se importa en bajas.module.ts

import { Component, Inject, OnInit, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Bajas } from "../../../models/personal/bajas.models";
import { BajasComponent } from "./bajas.component";
import { BajasService } from "../../../services/employees/bajas.service";
import { EmployeesListModel } from "../employees-list.model";
import { UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import moment from 'moment';
import { FicherosEmpleadosService } from "../../../services/employees/ficheros-empleados.service";
import { Subscription } from "rxjs";

@Component({
    selector: "app-bajas-form",
    templateUrl: "./bajas-form.component.html",
    styleUrls: ["./bajas-form.component.css"],
    standalone: false
})
export class BajasFormComponent implements OnInit, OnDestroy {
    // #region Propiedades del componente
    empleados: EmployeesListModel[] = [];
    form: UntypedFormGroup = null;
    bajas: Bajas = this.BajasService.newBajas();
    empleadoSeleccionado: EmployeesListModel = null;

    // Propiedades para manejo de archivos
    loadingFile: boolean = false;
    imageTempUrl: string = null;
    fileUid: number = null;
    fileGuid: string = null;
    isUploading: boolean = false; // Indicador de subida de archivo

    // Suscripciones
    private uploadSubscription: Subscription;
    // #endregion

    constructor(
        public dialogRef: MatDialogRef<BajasComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any,
        public dialog: MatDialog,
        protected snackBar: MatSnackBar,
        public BajasService: BajasService,
        private ficherosEmpleadosService: FicherosEmpleadosService
    ) { }

    // #region Métodos del componente

    cancel(): void {
        this.dialogRef.close();
    }

    ngOnInit() {
        this.empleados = this.data.empleados;

        if (this.data.bajas != undefined) {
            this.bajas = this.data.bajas;
        } else {
            this.bajas = this.BajasService.newBajas();
        }

        // Si hay un empleado seleccionado, establecerlo en el formulario
        if (this.data.empleadoSeleccionado) {
            this.empleadoSeleccionado = this.data.empleadoSeleccionado;
            this.bajas.employees_Id = this.empleadoSeleccionado.uid;
        }

        this.inicializarFormulario();

        // Debug: verificar que el formulario se inicialice correctamente
        // Suscribirse al observable de subida de archivo
        this.uploadSubscription = this.ficherosEmpleadosService.uploadingFile$.subscribe(
            (isUploading: boolean) => {
                this.isUploading = isUploading;
            }
        );
    }

    ngOnDestroy(): void {
        // Limpiar suscripciones para evitar memory leaks
        if (this.uploadSubscription) {
            this.uploadSubscription.unsubscribe();
        }
    }

    onSubmit() {
        if (this.form.valid) {
            // Actualizar los datos del objeto bajas con los valores del formulario
            this.bajas.employees_Id = this.form.value.employees_Id;
            this.bajas.inicio = this.form.value.inicio;
            this.bajas.fin = this.form.value.fin;
            this.bajas.motivo = this.form.value.motivo;

            if (this.bajas.id == 0) {
                // Crear nueva baja
                this.bajas.fechaSolicitud = moment().format("YYYY-MM-DD");
                this.bajas.users_id = 0;

                this.BajasService.create(this.bajas, this.fileUid, this.fileGuid).then(
                    (resultado) => {
                        this.dialogRef.close(resultado);
                    }
                );
            } else {
                // Actualizar baja existente
                this.BajasService.update(this.bajas, this.fileUid, this.fileGuid).then(
                    (resultado) => {
                        this.dialogRef.close(resultado);
                    }
                );
            }
        }
    }

    rechazar(): void {
        this.dialog
            .open(RechazarBajasComponent, {
                data: {
                    title: "¿Seguro que quiere RECHAZAR la solicitud de bajas?",
                },
            })
            .afterClosed()
            .subscribe((result: string) => {
                if (result.length > 0) {
                    // Por ahora, como no tenemos métodos de guardado en el servicio, solo cerramos el diálogo
                    this.snackBar.open("Solicitud de bajas RECHAZADA", null, {
                        duration: 3000,
                    });
                    this.dialogRef.close();
                }
            });
    }

    validar(): void {
        this.dialog
            .open(ValidarBajasComponent, {
                data: {
                    title: "¿Seguro que quiere VALIDAR la solicitud de bajas?",
                },
            })
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    // Por ahora, como no tenemos métodos de guardado en el servicio, solo cerramos el diálogo
                    this.snackBar.open("Solicitud de bajas VALIDADA", null, {
                        duration: 3000,
                    });
                    this.dialogRef.close();
                }
            });
    }

    deleteFile(): void {
        this.fileUid = null;
        this.fileGuid = null;
        this.snackBar.open("Documento eliminado correctamente", null, {
            duration: 3000,
        });
    }

    // Métodos para manejo de archivos
    async uploadFile(event) {
        this.imageTempUrl = null;
        const fileList: FileList = event.target.files;
        if (fileList.length > 0) {
            const file: File = fileList[0];

            this.fileUid = null;
            this.fileGuid = null;

            const idEmployee: number =
                this.form.value.employees_Id != null
                    ? this.form.value.employees_Id
                    : 0;

            this.ficherosEmpleadosService
                .guardarFicheroEmpleadoGeneral(idEmployee, file)
                .subscribe({
                    next: (response) => {
                        this.fileUid = response.id;
                        this.fileGuid = response.uid;
                        this.snackBar.open(
                            "Documento guardado correctamente",
                            null,
                            {
                                duration: 3000,
                            }
                        );
                    },
                    error: (error) => {
                        console.log("error", error);
                        this.snackBar.open(
                            "Error al subir el archivo",
                            null,
                            {
                                duration: 3000,
                            }
                        );
                    },
                    complete: () => {
                        // El indicador se maneja automáticamente desde el servicio
                    },
                });
        }
    }

    // #endregion

    // Método para convertir archivo a base64
    private convertFileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    // #region Métodos privados del componente

    private inicializarFormulario(): void {
        this.form = new UntypedFormGroup({
            employees_Id: new UntypedFormControl(
                this.bajas.employees_Id,
                Validators.required
            ),
            inicio: new UntypedFormControl(this.bajas.inicio, Validators.required),
            fin: new UntypedFormControl(this.bajas.fin),
            motivo: new UntypedFormControl(this.bajas.motivo, Validators.required),
        });
    }

    // #endregion
}

@Component({
    selector: "validar-bajas",
    templateUrl: "validar.component.html",
    standalone: false
})
export class ValidarBajasComponent implements OnInit {
    title: string = "";
    constructor(
        private dialogRef: MatDialogRef<ValidarBajasComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.title = this.data.title;
    }

    ngOnInit(): void {
        this.title = this.data.title;
    }

    rechazar(): void {
        this.dialogRef.close(false);
    }

    validar(): void {
        this.dialogRef.close(true);
    }
}

@Component({
    selector: "rechazar-bajas",
    templateUrl: "rechazar.component.html",
    standalone: false
})
export class RechazarBajasComponent implements OnInit {
    form: UntypedFormGroup = null;
    title: string = "";
    constructor(
        private dialogRef: MatDialogRef<RechazarBajasComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {
        this.title = this.data.title;

        this.form = new UntypedFormGroup({
            rechazo: new UntypedFormControl("", Validators.required),
        });
    }

    ngOnInit(): void {
        this.title = this.data.title;
    }

    rechazar(): void {
        if (this.form.valid) {
            this.dialogRef.close(this.form.value.rechazo);
            this.form.reset();
        }
    }

    cancel(): void {
        this.dialogRef.close("");
    }
  public trackByIndex(i: number) { return i; }

}
